import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, Target, Lightbulb, Sliders, Image, MessageSquare, Settings, Search, Link2, LogOut, User, Info } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const navItems = [
    { path: '/dashboard', icon: BarChart3, label: 'Dashboard' },
    { path: '/campaigns', icon: Target, label: 'Campaigns' },
    { path: '/recommendations', icon: Lightbulb, label: 'Recommendations' },
    { path: '/scenarios', icon: Sliders, label: 'Scenarios' },
    { path: '/competitive', icon: Search, label: 'Competitive' },
    { path: '/creative', icon: Image, label: 'Creative' },
    { path: '/chat', icon: MessageSquare, label: 'Chat' },
    { path: '/admin', icon: Settings, label: 'Admin' },
    { path: '/integrations', icon: Link2, label: 'Integrations' },
    { path: '/about', icon: Info, label: 'About' },
  ];

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to logout?')) return;
    
    try {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear local storage
    localStorage.removeItem('user');
    
    // Redirect to login
    navigate('/login');
  };

  return (
    <div className="app">
      <nav className="sidebar">
        <div className="sidebar-header">
          <h1 className="sidebar-title">Clariti</h1>
          <p className="sidebar-subtitle">AI Marketing Intelligence</p>
        </div>
        <ul className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/');
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
        <div className="sidebar-footer">
          {/* User Info */}
          {user.email && (
            <div style={{ padding: '12px 16px', marginBottom: '12px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                {user.picture ? (
                  <img src={user.picture} alt={user.name} style={{ width: '32px', height: '32px', borderRadius: '8px' }} />
                ) : (
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--primary-500)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>
                    {user.name?.charAt(0) || 'U'}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name || 'User'}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
                <span className="badge" style={{ background: user.role === 'admin' ? 'var(--error-500)' : 'var(--primary-500)', color: 'white', fontSize: '0.75rem', textTransform: 'capitalize' }}>
                  {user.role || 'Analyst'}
                </span>
                {user.auto_admin && <span className="badge" style={{ background: 'var(--accent-500)', color: 'white', fontSize: '0.75rem' }}>Auto-Admin</span>}
              </div>
            </div>
          )}
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="btn btn-secondary"
            style={{ width: '100%', justifyContent: 'center', padding: '12px', fontSize: '0.875rem', fontWeight: '600' }}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
          
          <div className="tenant-info" style={{ marginTop: '12px' }}>
            <span className="tenant-label">Tenant</span>
            <span className="tenant-name">TechCo Industries</span>
          </div>
        </div>
      </nav>
      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

export default Layout;
